import type { App } from 'vue'
import { createI18n } from 'vue-i18n'
import { useLocaleStoreWithOut } from '@/store/modules/locale'
import type { I18n, I18nOptions } from 'vue-i18n'
import { setHtmlPageLang } from './helper'

export let i18n: ReturnType<typeof createI18n>

const localeLoaders = {
  en: () => import('../../locales/en'),
  'zh-CN': () => import('../../locales/zh-CN'),
  ar: () => import('../../locales/ar'),
  pt: () => import('../../locales/pt'),
  id: () => import('../../locales/id')
}

type SupportedLocale = keyof typeof localeLoaders

const normalizeLocale = (lang: string): SupportedLocale => {
  const normalizedLang = lang === 'chinese_simplified' ? 'zh-CN' : lang
  return normalizedLang in localeLoaders ? (normalizedLang as SupportedLocale) : 'en'
}

const loadLocaleMessages = async (lang: SupportedLocale) => {
  const localeModule = await localeLoaders[lang]()
  return localeModule.default ?? {}
}

const createI18nOptions = async (): Promise<I18nOptions> => {
  const localeStore = useLocaleStoreWithOut()
  const locale = localeStore.getCurrentLocale
  const localeMap = localeStore.getLocaleMap

  // 根据当前语言动态加载翻译文件
  const lang = normalizeLocale(locale.lang)

  // 加载翻译文件
  const message = await loadLocaleMessages(lang)

  setHtmlPageLang(locale.serviceId || 'zh-CHS')

  return {
    legacy: false,
    locale: lang,
    fallbackLocale: lang,
    messages: {
      [lang]: message
    },
    availableLocales: localeMap.map((v) => v.lang),
    sync: true,
    silentTranslationWarn: true,
    missingWarn: false,
    silentFallbackWarn: true
  }
}

export const setupI18n = async (app: App<Element>) => {
  const options = await createI18nOptions()
  i18n = createI18n(options) as I18n
  app.use(i18n)
}

// 导出切换语言的函数
export const changeLanguage = async (lang: string) => {
  if (!i18n) return

  const actualLang = normalizeLocale(lang)

  // 动态加载翻译文件
  const messages = await loadLocaleMessages(actualLang)
  i18n.global.setLocaleMessage(actualLang, messages)
  ;(i18n.global.locale as { value: string }).value = actualLang
  setHtmlPageLang(actualLang)

  // 触发语言变化事件，让菜单和 tagsView 刷新标题
  window.dispatchEvent(new Event('language-changed'))
}
